import { observable } from 'mobx'
import * as firebase from 'firebase/app'
import 'firebase/storage'
import { Broadway } from '../lib/broadway/mp4'

firebase.initializeApp({
  apiKey: "AIzaSyDtK8JGqlAXBhm4m9YC5gjU5Z8qkAtGxAE",
  projectId: "video-hood",
  storageBucket: "video-hood.appspot.com",
})

const fetchWithProgress = (url, options) => new Promise((resolve, reject) => {
  const req = new XMLHttpRequest()
  req.open('GET', url, true)
  Object.assign(req, options)

  req.onload = _ => {
    resolve(req.response)
  }
  req.onerror = _ => {
    reject(Object.assign(new Error(req.responseText), {
      code: req.status,
    }))
  }

  req.send()
})

export const RESUME_ENCODER_EVENT = 'resumeEncoder'

const getSettings = () => {
  const params = (new URL(document.location)).searchParams;
  const isDownload = params.has('download');
  const isEdit = params.has('edit');
  const isFull = params.has('full');
  const compositionName = params.get('n');


  const mode = isFull ? 'full' : (isDownload ? 'download' : (isEdit ? 'edit' : 'default'));

  const settings = {
    mode,
    canEdit: mode === 'edit'  || mode === 'full',
    canDownload: mode === 'download' || mode  === 'full',
    compositionName,
    price: params.get('p') || 19,
    productLink: params.get('pl') || '',
  };

  return settings;
};

class Store {
  @observable animationUrl = null
  @observable videoUrl = null
  videoPreviewUrl = null

  animationData = null
  upperLeft = null
  upperRight = null
  lowerLeft = null
  lowerRight = null
  layersForAssets = null
  screenShotUrl = null
  screenShotWidth = 0
  screenShotHeight = 0

  compositionName = null

  @observable animationWidth = 0
  @observable animationHeight = 0
  @observable error = null
  @observable canRender = false
  @observable hideEditor = true
  @observable mode = 'default'
  @observable canDownload = false
  @observable canEdit = false
  @observable price = 19
  @observable productLink = ''

  constructor() {
    Object.assign(this, getSettings());

    void this.getAssets()
  }

  async getAssets() {
    const name = this.compositionName

    if (!name) {
      this.error = 'missing composition name'
      return
    }

    const videoName = name
    const animationName = name

    const storage = firebase.storage()
    try {
      this.videoUrl = await storage
        .ref(`/compositions/${videoName}.mp4`)
        .getDownloadURL()

      this.videoPreviewUrl = await storage
        .ref(`/compositions/${videoName} For Preview.mp4`)
        .getDownloadURL()

      this.animationUrl = await storage
        .ref(`/compositions/${animationName}.json`)
        .getDownloadURL()
    } catch (e) {
      this.error = e.message
      return
    }

    void this.loadAnimation()
  }

  getNullLayersFromAnimationData(animationData) {
    const nullLayers = animationData
      .layers
      .filter(({ ty: type }) => type === 3)
      .map(({ nm: name, ks: keyframes }) => {
        const kfMapper = (keyframe) => {
          const { t: frame, s } = keyframe
          const start = s;
          const returnValue =  start && ({
            frame: frame + 1,
            // x: (start[0] + (end[0] || start[0])) / 2,
            // y: (start[1] + (end[1] || start[1])) / 2,
            x: start[0],
            y: start[1],
          });
          return returnValue;
        }

        const mappedKeyframes = keyframes.p.k.map(kfMapper)

        const firstKeyFrame = {
          frame: keyframes.p.k[0].t,
          x: keyframes.p.k[0].s[0],
          y: keyframes.p.k[0].s[1],
        };

        return {
          name,
          positions: mappedKeyframes.concat(firstKeyFrame),
        };
      });

      return nullLayers;
  }
  
  getEdges(nullLayers) {
    const expressions = {
      upperLeft: /upper\s*left/i,
      upperRight: /upper\s*right/i,
      lowerLeft: /lower\s*left/i,
      lowerRight: /lower\s*right/i,
    }

    const edges = {};

    Object.entries(expressions)
      .forEach(([key, exp]) => {
        edges[key] = nullLayers
          .filter(({ name }) => exp.test(name))
          .reduce((result, { positions }) => [
            ...result,
            ...positions,
          ], [])
          .filter(position => position)
          .reduce((result, { frame, x, y }) => ({
            ...result,
            [frame]: { x, y },
          }), {})
      })

      return edges;
  }

  getLayersForAssets(animationData) {
    const assets = animationData
    .assets
    .reduce((result, asset) => ({
      ...result,
      [asset.id]: asset,
    }), {})

    return animationData
      .layers
      .filter(({ refId }) => refId in assets)
      .map(layer => ({
        layer,
        asset: assets[layer.refId],
      }))
  }

  initScreenShot(layersForAssets) {
    const screenShotLayer = layersForAssets
      .find(({ layer }) => /screenshot/i.test(layer.nm))
    if (screenShotLayer) {
      this.screenShotUrl = screenShotLayer.asset.p
      this.screenShotWidth = screenShotLayer.asset.w
      this.screenShotHeight = screenShotLayer.asset.h
    }
  }

  async loadAnimation() {
    const res = await fetch(this.animationUrl, {
      mode: 'cors',
    })
    const animationData = await res.json()

    const nullLayers = this.getNullLayersFromAnimationData(animationData);

    const edges = this.getEdges(nullLayers);
    Object.assign(this, edges);
    this.layersForAssets = this.getLayersForAssets(animationData);
    this.initScreenShot(this.layersForAssets)

    this.animationData = animationData
    this.animationWidth = animationData.w
    this.animationHeight = animationData.h
  }

  audioSampleRate = null
  numberOfAudioChannels = null
  leftChannel = null
  rightChannel = null

  @observable previewVideoLoadedBytes = null
  @observable previewVideoTotalBytes = null

  loadPreview = () => fetchWithProgress(this.videoPreviewUrl, {
    responseType: 'blob',
    onprogress: ({ loaded, total }) => {
      this.previewVideoLoadedBytes = loaded
      this.previewVideoTotalBytes = total
    }
  })

  previewFrameCache = []
  previewTotalFrames = null

  handlePreviewTotalFrames = frames => {
    this.previewTotalFrames = frames
  }

  @observable isFrameCacheReady = false
  @observable cacheProgress = 0
  @observable isFirstFrameCached = false
  previewFrameWidth = null
  previewFrameHeight = null

  handlePreviewFrameDecoded = (buffer, width, height) => {
    this.previewFrameCache.push(buffer)

    if (this.previewFrameCache.length === 1) {
      this.previewFrameWidth = width
      this.previewFrameHeight = height
      this.isFirstFrameCached = true
    }

    if (this.previewFrameCache.length % 100 === 0) {
      this.cacheProgress = this.previewFrameCache.length * 100 / this.previewTotalFrames
    }
    if (this.previewFrameCache.length >= this.previewTotalFrames) {
      this.isFrameCacheReady = true
    }
  }

  videoFrameCache = {}
  videoFrameWidth = null
  videoFrameHeight = null
  events = new window.EventTarget()

  handleVideoFrameDecoded = (buffer, width, height) => {
    this.videoFrameCache[this.videoLoader.player.decodedFrame] = buffer
    this.videoFrameWidth = width
    this.videoFrameHeight = height

    this.events.dispatchEvent(new CustomEvent(RESUME_ENCODER_EVENT))
  }

  videoTotalFrames = null
  handleVideoTotalFrames = frames => {
    this.videoTotalFrames = frames
  }

  videoLoader = null
  isVideoDecoderBusy = false
  async getNextVideoFrames(batchSize) {
    if (this.isVideoDecoderBusy) {
      return
    }

    this.isVideoDecoderBusy = true

    if (!this.videoLoader) {
      const buffer = await fetchWithProgress(this.videoUrl, {
        responseType: 'arraybuffer',
      })

      this.videoLoader = new Broadway(buffer)
      this.videoLoader.player.onTotalFrames = this.handleVideoTotalFrames
      this.videoLoader.player.onFrameDecoded = this.handleVideoFrameDecoded
      this.videoLoader.player.start()
    }

    await this.videoLoader.player.decodeNextFrames(batchSize)
    this.isVideoDecoderBusy = false
  }

  deocdedAudioBuffer = null
  async loadSoundtrack() {
    const blob = await this.loadPreview()
    const buffer = await (new Response(blob)).arrayBuffer()

    const bw = new Broadway(buffer)
    bw.player.onFrameDecoded = this.handlePreviewFrameDecoded
    bw.player.onTotalFrames = this.handlePreviewTotalFrames
    bw.player.start()
    bw.player.decodeNextFrames()
/*
    let frame = 0
    let timeStarted = Date.now()
    decoder.onPictureDecoded = () => {
      console.log('Decoder', frame, Date.now() - timeStarted)
      frame++
      timeStarted = Date.now()
    }
    decoder.decode(buffer)
*/
    const audioCtx = new AudioContext()

    const decodedAudioBuffer = await audioCtx.decodeAudioData(buffer)
    this.deocdedAudioBuffer = decodedAudioBuffer
    const { sampleRate, numberOfChannels } = decodedAudioBuffer
    this.audioSampleRate = sampleRate
    this.numberOfAudioChannels = numberOfChannels
    this.leftChannel = decodedAudioBuffer.getChannelData(0)
    this.rightChannel = numberOfChannels > 1
      ? decodedAudioBuffer.getChannelData(numberOfChannels - 1)
      : this.leftChannel

    return blob
  }
}

export default Store



// WEBPACK FOOTER //
// ./src/store/index.js